/**
 * FEATURE FLAGS — Source unique de vérité
 *
 * Pour activer une feature : passer à true.
 * Pour la désactiver sans deploy : passer à false.
 */
export const FEATURE_FLAGS = {
  // Prêt — déjà implémenté
  ACHIEVEMENTS: true,
  WELLNESS: true,
  PROGRESSION_CHARTS: true,
  MERIT_WAITLIST: true,
  PROGRAM_EXPORT_PDF: true,

  // À activer quand prêt — nécessite configuration externe
  BILLING_STRIPE: false,
  CALENDAR_SYNC_ICAL: true,
  CALENDAR_SYNC_GOOGLE: false,
  MERIT_SHOP: true,
  SOCIAL_FRIENDS: true,
  BRAIN_ROUTER: false,
  NUTRITION_PLANNING: true,
  NUTRITION_SUPPLEMENTS: false,
  SUBSCRIPTION_GATING: false,
  SUBSCRIPTION_STATUS_UI: false,

  // V2 future
  SCIENCE_CARDS: false,
  AVATAR: false,
  CHALLENGES_UI: false,
  WELLNESS_PREVENTION: false,
  LIBRARY_TOPICS: false,
  PRO_MARKETPLACE: false,
  BRAIN_SESSIONS: false,

  // V3 — Scaffolded future modules (default OFF)
  SOCIAL_LINKS_HUB: false,
  MERIT_ECONOMY_V2: false,
  SUSPICION_SIGNALS: false,
  PROFILE_UPDATE_PROMPTS: false,
  BRAIN_PREFERENCES: false,
  NUTRITION_PREFERENCES_V2: false,
  BRAIN_POPUPS: false,
  FEATURE_UPDATE_ALERTS: false,
  VIDEO_ANALYSIS_CONTEXT: false,
  PARENT_COACHING: false,
  ADMIN_WEAKPOINT_ESCALATION: false,
  OPTIONAL_EXTRAS: false,
  REFERRAL_SYSTEM: false,
  PROMO_CODES: false,
  BUILD_GUIDE: false,
  IP_PROTECTION: false,
  MENTAL_TRAINING: false,
  BRAIN_METHODS_LIBRARY: false,
  KNOWLEDGE_GAP_ASSISTANT: false,
  ADMIN_HEALTH_BRAIN: false,
  NUTRITION_KNOWLEDGE: false,
  PRIVACY_ALERTS: false,
  DATA_SECURITY: false,
  PROMPT_SYSTEM: false,
  BRAIN_ARCHITECTURE: false,
  MANUAL_MODE: false,
  FUTURE_PATHS: false,
  XP_SYSTEM: false,

  // V4 — Expansion paths (default OFF)
  VISUAL_MODEL_PATHS: false,
  ATHLETE_COMPARISON: false,
  EXPERIENCE_MODE: false,
  INTERFACE_MODES: false,
  FAVORITES_SYSTEM: false,
  MERIT_PRICE_RULES: false,
  INTERNAL_COMMERCE: false,
  MERIT_MARKETPLACE: false,
  BETA_ACCESS: false,
  MODULE_TUTORIALS: false,

  // Questionnaire system
  QUESTIONNAIRE_HUB: true,

  // Female health — Cycle tracking (P1 foundation)
  FEMALE_HEALTH: true,

  // Admin tools
  ADMIN_REVIEW_HUB: false,
} as const;

export type FeatureFlag = keyof typeof FEATURE_FLAGS;

export function isEnabled(flag: FeatureFlag): boolean {
  return FEATURE_FLAGS[flag];
}

// DEV-only: cross-reference roadmap flagKeys against existing flags
if (import.meta.env.DEV) {
  import("@/lib/roadmap").then(({ ROADMAP }) => {
    ROADMAP
      .filter(e => e.flagKey && e.status !== 'done')
      .forEach(entry => {
        if (!(entry.flagKey! in FEATURE_FLAGS)) {
          console.warn(
            `[ROADMAP] Missing flag in featureFlags.ts: ${entry.flagKey} ` +
            `(path: ${entry.id} — ${entry.title})`
          );
        }
      });
  });

  // DEV-only: cross-reference shop module items vs DB CHECK constraint whitelist
  // Manually synced with the latest migration on point_transactions_type_check.
  // When you add a new `*_module_unlock` shop item, ALSO add `spend_shop_<id>`
  // to the DB CHECK constraint AND to this list.
  import("@/constants/meritShop").then(({ MERIT_SHOP_ITEMS }) => {
    const DB_WHITELISTED_SHOP_TYPES = new Set([
      "spend_shop_nutrition_module_unlock",
      "spend_shop_social_module_unlock",
      "spend_shop_wellness_module_unlock",
      "spend_shop_avatar_frame_bronze",
      "spend_shop_avatar_frame_silver",
      "spend_shop_avatar_frame_gold",
      "spend_shop_profile_theme_dark_pro",
      "spend_shop_extra_brain_questions",
      "spend_shop_science_card_pack_common",
      "spend_shop_science_card_pack_rare",
    ]);
    MERIT_SHOP_ITEMS
      .filter(item => item.id.endsWith("_module_unlock") && item.isAvailable)
      .forEach(item => {
        const expected = `spend_shop_${item.id}`;
        if (!DB_WHITELISTED_SHOP_TYPES.has(expected)) {
          console.warn(
            `[MERIT_SHOP] Item "%s" vendable mais type DB potentiellement non whitelisté: %s`,
            item.id,
            expected
          );
        }
      });
  });
}
